import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor", "editorContainer", "hiddenInput"]

  connect() {
    this.loadQuill()
  }

  disconnect() {
    if (this.quill) {
      this.quill = null
    }
  }

  async loadQuill() {
    // Load Quill CSS
    if (!document.querySelector('link[href*="quill"]')) {
      const link = document.createElement("link")
      link.rel = "stylesheet"
      link.href = "https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.snow.css"
      document.head.appendChild(link)
    }

    // Load Quill JS
    if (!window.Quill) {
      const script = document.createElement("script")
      script.src = "https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.js"
      script.onload = () => this.initializeQuill()
      document.head.appendChild(script)
    } else {
      this.initializeQuill()
    }
  }

  initializeQuill() {
    if (!this.hasEditorContainerTarget) return

    // Build custom toolbar with merge field and form field buttons
    const toolbarOptions = {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link'],
        ['clean'],
        ['mergefield', 'formfield']
      ],
      handlers: {
        'mergefield': () => this.showMergeFieldMenu(),
        'formfield': () => this.showFormFieldMenu()
      }
    }

    this.quill = new Quill(this.editorContainerTarget, {
      theme: 'snow',
      modules: {
        toolbar: toolbarOptions
      }
    })

    // Set initial content from hidden input
    if (this.hasHiddenInputTarget && this.hiddenInputTarget.value) {
      this.quill.root.innerHTML = this.hiddenInputTarget.value
    }

    // Sync content to hidden input on change
    this.quill.on('text-change', () => {
      if (this.hasHiddenInputTarget) {
        this.hiddenInputTarget.value = this.quill.root.innerHTML
      }
    })

    // Add custom button labels
    this.customizeToolbarButtons()

    // Add custom styles for contract elements
    this.addCustomStyles()
  }

  customizeToolbarButtons() {
    const toolbar = this.editorContainerTarget.previousElementSibling
    if (!toolbar) return

    const mergeBtn = toolbar.querySelector('.ql-mergefield')
    if (mergeBtn) {
      mergeBtn.innerHTML = '<span style="font-size: 12px;">+ Merge Field</span>'
      mergeBtn.style.width = 'auto'
      mergeBtn.style.padding = '0 8px'
    }

    const formBtn = toolbar.querySelector('.ql-formfield')
    if (formBtn) {
      formBtn.innerHTML = '<span style="font-size: 12px;">+ Form Field</span>'
      formBtn.style.width = 'auto'
      formBtn.style.padding = '0 8px'
    }
  }

  addCustomStyles() {
    if (document.querySelector('#contract-editor-styles')) return

    const style = document.createElement('style')
    style.id = 'contract-editor-styles'
    style.textContent = `
      .ql-editor {
        min-height: 500px;
        font-size: 14px;
      }
      .ql-editor .merge-field {
        background: #fef3c7;
        padding: 2px 6px;
        border-radius: 4px;
        font-family: monospace;
      }
      .ql-editor .contract-signature,
      .ql-editor .contract-text,
      .ql-editor .contract-textarea,
      .ql-editor .contract-yesno,
      .ql-editor .contract-checkbox,
      .ql-editor .contract-date {
        display: inline-block;
        border: 2px dashed #9ca3af;
        padding: 4px 8px;
        background: #f3f4f6;
        border-radius: 4px;
        margin: 2px;
      }
      .ql-editor .contract-signature {
        min-width: 200px;
        min-height: 40px;
      }
      .ql-editor .contract-textarea {
        display: block;
        min-width: 300px;
        min-height: 60px;
      }
      .merge-field-menu, .form-field-menu {
        position: absolute;
        background: white;
        border: 1px solid #ccc;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 1000;
        max-height: 400px;
        overflow-y: auto;
        min-width: 220px;
      }
      .merge-field-menu button, .form-field-menu button {
        display: block;
        width: 100%;
        text-align: left;
        padding: 8px 16px;
        border: none;
        background: none;
        cursor: pointer;
        font-size: 14px;
      }
      .merge-field-menu button:hover, .form-field-menu button:hover {
        background: #f3f4f6;
      }
      .merge-field-menu .category {
        padding: 6px 16px;
        font-size: 11px;
        font-weight: 600;
        color: #6b7280;
        background: #f9fafb;
        text-transform: uppercase;
      }
    `
    document.head.appendChild(style)
  }

  showMergeFieldMenu() {
    this.closeMenus()

    const fields = {
      "Customer": [
        { value: "customer_name", label: "Customer Name" },
        { value: "customer_email", label: "Customer Email" },
        { value: "customer_phone", label: "Customer Phone" }
      ],
      "Pet": [
        { value: "pet_names", label: "Pet Names" },
        { value: "pet_breeds", label: "Pet Breeds" },
        { value: "pet_species", label: "Pet Species" }
      ],
      "Booking": [
        { value: "booking_dates", label: "Booking Dates" },
        { value: "check_in_date", label: "Check-in Date" },
        { value: "check_out_date", label: "Check-out Date" },
        { value: "service_name", label: "Service Name" }
      ],
      "Location": [
        { value: "location_name", label: "Location Name" },
        { value: "location_phone", label: "Location Phone" }
      ],
      "Order": [
        { value: "order_total", label: "Order Total" },
        { value: "order_paid", label: "Amount Paid" },
        { value: "order_due", label: "Amount Due" }
      ],
      "Signing": [
        { value: "signer_name", label: "Signer Name" },
        { value: "current_date", label: "Current Date" }
      ]
    }

    const menu = document.createElement('div')
    menu.className = 'merge-field-menu'

    for (const [category, categoryFields] of Object.entries(fields)) {
      const catDiv = document.createElement('div')
      catDiv.className = 'category'
      catDiv.textContent = category
      menu.appendChild(catDiv)

      for (const field of categoryFields) {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.textContent = field.label
        btn.onclick = () => {
          this.insertMergeField(field.value)
          menu.remove()
        }
        menu.appendChild(btn)
      }
    }

    this.positionMenu(menu)
    document.body.appendChild(menu)

    // Close on outside click
    setTimeout(() => {
      document.addEventListener('click', this.closeMenusHandler = () => {
        menu.remove()
        document.removeEventListener('click', this.closeMenusHandler)
      }, { once: true })
    }, 0)
  }

  showFormFieldMenu() {
    this.closeMenus()

    const fields = [
      { type: 'signature', label: 'Signature Pad' },
      { type: 'text', label: 'Text Input' },
      { type: 'textarea', label: 'Text Area' },
      { type: 'yesno', label: 'Yes / No' },
      { type: 'checkbox', label: 'Checkbox' },
      { type: 'date', label: 'Date Picker' }
    ]

    const menu = document.createElement('div')
    menu.className = 'form-field-menu'

    for (const field of fields) {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.textContent = field.label
      btn.onclick = () => {
        this.insertFormField(field.type)
        menu.remove()
      }
      menu.appendChild(btn)
    }

    this.positionMenu(menu)
    document.body.appendChild(menu)

    setTimeout(() => {
      document.addEventListener('click', this.closeMenusHandler = () => {
        menu.remove()
        document.removeEventListener('click', this.closeMenusHandler)
      }, { once: true })
    }, 0)
  }

  positionMenu(menu) {
    const toolbar = this.editorContainerTarget.previousElementSibling
    const rect = toolbar.getBoundingClientRect()
    menu.style.top = `${rect.bottom + window.scrollY + 4}px`
    menu.style.left = `${rect.right - 240}px`
  }

  closeMenus() {
    document.querySelectorAll('.merge-field-menu, .form-field-menu').forEach(m => m.remove())
  }

  insertMergeField(fieldName) {
    const range = this.quill.getSelection(true)
    this.quill.insertText(range.index, `{{${fieldName}}}`, { 'background': '#fef3c7' })
    this.quill.setSelection(range.index + fieldName.length + 4)
  }

  insertFormField(type) {
    const fieldName = prompt("Enter field name (e.g., emergency_contact):")
    if (!fieldName) return

    let label = fieldName
    if (['text', 'textarea', 'yesno', 'checkbox'].includes(type)) {
      label = prompt("Enter label (displayed to user):") || fieldName
    }

    let html = ''
    switch (type) {
      case 'signature':
        html = `<span class="contract-signature" data-field="${fieldName}">[Signature: ${fieldName}]</span>`
        break
      case 'text':
        html = `<span class="contract-text" data-field="${fieldName}" data-label="${label}">[${label}]</span>`
        break
      case 'textarea':
        html = `<div class="contract-textarea" data-field="${fieldName}" data-label="${label}">[${label}]</div>`
        break
      case 'yesno':
        html = `<span class="contract-yesno" data-field="${fieldName}" data-label="${label}">[Yes/No: ${label}]</span>`
        break
      case 'checkbox':
        html = `<span class="contract-checkbox" data-field="${fieldName}" data-label="${label}">[Checkbox: ${label}]</span>`
        break
      case 'date':
        html = `<span class="contract-date" data-field="${fieldName}">[Date]</span>`
        break
    }

    const range = this.quill.getSelection(true)
    this.quill.clipboard.dangerouslyPasteHTML(range.index, html)
  }
}
